// Команды: имя правится на месте, состав — чипами; порядок чипов — порядок подстановки в карточке задачи.
import { Fragment, useCallback, useMemo } from 'react';
import { useTable } from '@core/store';
import { activePeople } from '@core/people';
import { cn } from '@core/ui/cn';
import { EntitySelect } from '@core/ui/EntitySelect';
import { ChipGhostMark, useChipReorder } from '@core/ui/ChipReorder';
import type { Row } from '@core/store/model';
import { ConfirmX, InlineText, TD, TH } from './cells';
import { matches, planTeam, planTeamMembers, type Runner } from './planner';

/** Состав команды чипами: × убирает, перетаскивание и Alt+←/→ переставляют, порядок — порядок подстановки в карточке. */
function TeamMembers({ team, mine, people, run, busy, members, free }: {
  team: Row<'team'>; mine: Row<'team_member'>[]; people: Map<string, Row<'person'>>; run: Runner; busy: boolean;
  members: Map<string, Row<'team_member'>>; free: Row<'person'>[];
}) {
  const ids = useMemo(() => mine.map((m) => m.person_id), [mine]);
  const save = useCallback((next: string[]) => { void run(planTeamMembers(team.id, next, members.values())); }, [run, team.id, members]);
  const { chipProps, ghost } = useChipReorder(ids, save);
  const ghostMark = ghost ? <ChipGhostMark label={people.get(ghost.id)?.name ?? ''} className="py-0.5 text-xs" /> : null;
  return (
    <span className="flex flex-wrap items-center gap-1">
      {mine.map((m) => {
        const p = people.get(m.person_id);
        const cp = chipProps(m.person_id);
        return (<Fragment key={m.id}>
          {ghost?.before === m.person_id && ghostMark}
          <span {...cp} className={cn('inline-flex items-center gap-1 rounded-full border border-gray-300 bg-white pl-2 pr-1 py-0.5 text-xs', cp.className)} data-admin-team-member={m.person_id}>
            <span className="inline-block w-2 h-2 rounded-full" style={{ background: p?.color ?? '#999' }} aria-hidden />{p?.name ?? '?'}
            <button type="button" className="min-h-0 w-4 h-4 rounded-full hover:bg-gray-200 leading-none text-ink-muted" aria-label={`Remove ${p?.name ?? ''} from ${team.name}`} disabled={busy}
              onClick={() => save(ids.filter((x) => x !== m.person_id))}>×</button>
          </span>
        </Fragment>);
      })}
      {ghost?.before === null && ghostMark}
      {mine.length === 0 && <span className="text-xs text-danger">empty</span>}
      <EntitySelect action size="sm" className="w-32" label={`Add to ${team.name}`} placeholder="+ add" disabled={busy || free.length === 0} data={{ 'data-admin-team-add': team.id }}
        options={free.map((p) => ({ id: p.id, name: p.name, color: p.color, person: true }))} value={null} onChange={(id) => { if (id) save([...ids, id]); }} />
    </span>
  );
}

export function TeamsBlock({ query, run, busy, adding, onAdded }: { query: string; run: Runner; busy: boolean; adding: boolean; onAdded: () => void }) {
  const teams = useTable('team');
  const members = useTable('team_member');
  const people = useTable('person');
  const list = useMemo(() => [...teams.values()].filter((t) => matches(query, t.name)).sort((a, b) => a.position - b.position || a.name.localeCompare(b.name)), [teams, query]);
  const active = useMemo(() => activePeople(people.values()), [people]);
  const membersOf = (teamId: string) => [...members.values()].filter((m) => m.team_id === teamId).sort((a, b) => a.position - b.position);

  return (
    <section data-admin-teams>
      <table className="text-sm border-collapse max-w-4xl">
        <thead>
          <tr className="border-b border-gray-200">
            <th className={cn(TH, 'w-8')} aria-label="Delete team" />
            <th className={cn(TH, 'w-48')} title="Click to rename">Team</th>
            <th className={TH} title="A team fills the assignee list in one click, in this order; empty teams are hidden in a task card">Members</th>
          </tr>
        </thead>
        <tbody>
          {adding && (
            <tr className="border-b border-gray-100 bg-accent/5" data-admin-new-team>
              <td className={TD} colSpan={3}>
                <InlineText value="" placeholder="New team — name, Enter to add" editing onCancel={onAdded} data={{ 'data-admin-new-team-name': '' }}
                  onSave={async (name) => { const ok = await run(planTeam(name, undefined, teams.values())); if (ok) onAdded(); return ok; }} />
              </td>
            </tr>
          )}
          {list.map((t) => {
            const mine = membersOf(t.id);
            const ids = mine.map((m) => m.person_id);
            const free = active.filter((p) => !ids.includes(p.id));
            return (
              <tr key={t.id} data-admin-team={t.id} className="border-b border-gray-100 hover:bg-gray-50">
                <td className={cn(TD, 'w-8')}>
                  <ConfirmX label={`Delete ${t.name}`} busy={busy} question={<>Delete {t.name}?</>} data={{ 'data-admin-team-delete': t.id }}
                    onConfirm={() => void run({ changes: [{ table: 'team', id: t.id, delete: true }], op: { kind: 'delete_team', name: t.name } })} />
                </td>
                <td className={cn(TD, 'w-48 font-medium')}>
                  <InlineText value={t.name} data={{ 'data-admin-team-name': t.id }} onSave={(name) => run(planTeam(name, t, teams.values()))} />
                </td>
                <td className={TD}>
                  <TeamMembers team={t} mine={mine} people={people} run={run} busy={busy} members={members} free={free} />
                </td>
              </tr>
            );
          })}
          {list.length === 0 && !adding && <tr><td className={cn(TD, 'text-ink-muted py-3')} colSpan={3}>No teams{query ? ' match' : ' yet'}.</td></tr>}
        </tbody>
      </table>
    </section>
  );
}
